<script setup lang="ts">
import { computed } from 'vue'
import SiteBrandSymbol from '../../SiteBrandSymbol.vue'
import { resolveHeaderActions } from '../../header-actions'
import type { HeaderBlock } from '../../types'
import SiteHeaderActions from '../../SiteHeaderActions.vue'
import SiteLink from '../../SiteLink.vue'
import SiteNavigation from '../../SiteNavigation.vue'
import { useWebsiteNavigation } from '../../website-navigation-context'

const props = defineProps<{ block: HeaderBlock }>()
const logoStyle = computed(() => {
  const width = props.block.content.logo?.width

  return { width: `var(--site-logo-width, ${typeof width === 'number' && width > 0 ? `${width}px` : '170px'})`, maxWidth: '100%' }
})
const brandLockupClass = computed(() => props.block.content.logo?.symbol ? 'site:gap-3' : '')
const brandLink = computed(() => props.block.content.brandLink ?? {
  label: props.block.content.businessName,
  destination: { type: 'page' as const, path: '/' },
})
const navigation = useWebsiteNavigation(
  () => props.block.content.navigationId,
  () => props.block.content.navigation,
)
const actions = computed(() => resolveHeaderActions(props.block.content))
const isTransparent = computed(() => props.block.settings.surface === 'transparent')
const headerClasses = computed(() => ({
  'site-header--dark': !isTransparent.value,
  'site-header--floating': props.block.settings.floating,
  'site-header--glass': props.block.settings.surface === 'glass',
  'site-header--light': isTransparent.value,
  'site-header--sticky': props.block.settings.sticky,
  'site-header--transparent': isTransparent.value,
}))
const navigationTone = computed(() => isTransparent.value ? 'light' : 'dark')

</script>

<template>
  <header
    class="site-header"
    :class="headerClasses"
  >
    <div class="site-header__surface site-site-container site:flex site:min-h-24 site:items-stretch site:justify-between site:gap-8 site:px-4 site:py-3">
      <SiteLink class="site-heading site:flex site:items-center site:text-xl site:font-bold" :class="brandLockupClass" :link="brandLink">
        <SiteBrandSymbol v-if="block.content.logo?.symbol" :logo="block.content.logo" :name="block.content.brandLink?.label ?? block.content.businessName" />
        <img
          v-else-if="block.content.logo"
          :src="block.content.logo.src"
          :alt="block.content.logo.alt"
          :style="logoStyle"
          class="site:h-auto site:max-w-full site:object-contain"
        />
        <template v-else>{{ block.content.brandLink?.label ?? block.content.businessName }}</template>
      </SiteLink>
      <div class="header-split__actions">
        <SiteHeaderActions :actions="actions" mode="small" />
        <SiteNavigation :navigation="navigation" :tone="navigationTone" />
        <SiteHeaderActions :actions="actions" mode="desktop" />
      </div>
    </div>
  </header>
</template>

<style scoped>
.header-split__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

@container site-renderer (min-width: 64rem) {
  .header-split__actions {
    align-items: stretch;
    gap: 2rem;
  }
}
</style>
